import {
    useStyles,
    Header,
    useAppDisplayModeContext,
} from '@archipro-design/aria';
import type { NavigationItem } from '@modules/elemental-page/type';
import * as S from './ElementalPageNavigation.style';
import { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { useMatchesHandleData } from '~/modules/root';

interface ElementalPageNavigationProps {
    pageTitle: string;
    navigationItems: NavigationItem[];
}

const ElementalPageNavigation = ({
    pageTitle,
    navigationItems,
}: ElementalPageNavigationProps) => {
    const { css } = useStyles();
    const currentYear = new Date().getFullYear().toString();
    const [activeAnchor, setActiveAnchor] = useState(
        navigationItems.length ? (navigationItems[0]?.anchor ?? null) : null,
    );
    const navRef = useRef<HTMLDivElement>(null);
    const { mobile } = useAppDisplayModeContext();

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry?.isIntersecting) {
                setActiveAnchor(
                    navigationItems.length > 0
                        ? (navigationItems[0]?.anchor ?? null)
                        : null,
                );
            }
        });

        const currentNavRef = navRef.current;

        if (currentNavRef) {
            observer.observe(currentNavRef);
        }

        return () => {
            if (currentNavRef) {
                observer.unobserve(currentNavRef);
            }
        };
    }, [navigationItems]);

    const smoothAnchoring = (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        anchor: string,
    ) => {
        e.preventDefault();
        const targetElement = document.getElementById(anchor);
        if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const hideSideNavHead = useMatchesHandleData('hideSideNavHead', false);
    const hideSideNavOnMobile = useMatchesHandleData(
        'hideSideNavOnMobile',
        false,
    );

    if (hideSideNavOnMobile && mobile) {
        return null;
    }

    return (
        <div className={css(S.SideNav)} ref={navRef}>
            {!hideSideNavHead && (
                <div className={css(S.NavHead)}>
                    <Header variant={'01'}>{pageTitle}</Header>
                    <Header variant={'02'}>{currentYear}</Header>
                </div>
            )}
            <ul className={css(S.NavBody)}>
                {navigationItems &&
                    navigationItems.map((item) => (
                        <li
                            key={`nav-item-${item.anchor}`}
                            className={classnames(
                                css(S.NavItem),
                                activeAnchor === item.anchor
                                    ? 'active'
                                    : 'not-active',
                            )}
                            onClick={() => setActiveAnchor(item.anchor)}
                        >
                            <a
                                className={css(S.NavLink)}
                                href={`#${item.anchor}`}
                                onClick={(e) => smoothAnchoring(e, item.anchor)}
                            >
                                {item.navigationLabel}
                            </a>
                        </li>
                    ))}
            </ul>
        </div>
    );
};

export default ElementalPageNavigation;
